import { useCallback, useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { clearChannelMarkup } from 'api/channelMarkup'
import Sheet from 'components/utils/Sheet'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLEStaffOrAdmin } from 'selectors/accountSelectors'
import { getChannelMarkup } from 'selectors/channelMarkupSelector'
import zIndex from 'styles/tools/z-index'

const Container = styled(Sheet)`
  position: sticky;
  top: 0;
  z-index: ${zIndex.spoofingWarning};
`

function reloadWithoutUtmParams() {
  const url = new URL(window.location.href)
  const keysToRemove: Array<string> = []
  url.searchParams.forEach((_, key) => {
    if (key.toLowerCase().startsWith('utm_')) {
      keysToRemove.push(key)
    }
  })
  keysToRemove.forEach(key => url.searchParams.delete(key))
  window.location.replace(url.toString())
}

function ChannelMarkupStaffBanner() {
  const intl = useIntl()
  const isStaff = useAppSelector(isLEStaffOrAdmin)
  const channelMarkup = useAppSelector(getChannelMarkup)
  const [isClearing, setIsClearing] = useState(false)

  const handleExit = useCallback(async() => {
    setIsClearing(true)
    try {
      await clearChannelMarkup()
      reloadWithoutUtmParams()
    } catch {
      setIsClearing(false)
    }
  }, [])

  if (!isStaff || !channelMarkup?.channelMarkupValue || channelMarkup.channelMarkupValue === 1) {
    return null
  }

  const adjustmentPercent = Math.round(Math.abs(channelMarkup.channelMarkupValue - 1) * 100)
  const isMarkup = channelMarkup.channelMarkupValue > 1

  return <Container
    className="no-print"
    background="critical"
    paletteType="inverse"
    padding={8}
  >
    <Group
      direction="vertical"
      tabletDirection="horizontal"
      horizontalAlign="space-between"
      verticalAlign="center"
      gap={8}
    >
      <BodyText variant="medium" weight="bold" colour="neutral-one" align="center">
        {isMarkup && <FormattedMessage
          id="ztL1XjPAOEXDSZesCsEq"
          defaultMessage="Affiliate markup active — prices include a {markupPercent}% markup."
          description="Staff-only banner shown when channel-level affiliate markup is active in the session"
          values={{ markupPercent: adjustmentPercent }}
        />}
        {!isMarkup && <FormattedMessage
          id="AYK3FgynVxTvgBS1n7vO"
          defaultMessage="Affiliate pricing active — prices include a {discountPercent}% discount."
          description="Staff-only banner shown when channel-level affiliate pricing applies a discount in the session"
          values={{ discountPercent: adjustmentPercent }}
        />}
      </BodyText>
      <TextButton
        kind="primary"
        onClick={handleExit}
        disabled={isClearing}
      >
        {intl.formatMessage({
          id: '8RsNvhcSvQcWylSpLJ3a',
          defaultMessage: 'Exit markup session',
          description: 'Staff button to clear channel markup cookie and reload the page',
        })}
      </TextButton>
    </Group>
  </Container>
}

export default ChannelMarkupStaffBanner
